
import { AnimatePresence, motion } from 'framer-motion';
import {
  getRegionBySlug,
  getCouncilLeadership,
  fetchRegionEvents,
  fetchRegionPublications,
  fetchRegionPressReleases,
  fetchRegionPressCoverage,
} from './regionApi';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  FaBolt, FaBuilding, FaCalendarAlt, FaCertificate,
  FaChartLine, FaCheck, FaChevronLeft, FaChevronRight,
  FaDownload, FaEnvelope, FaExchangeAlt, FaFileAlt, FaFileSignature, FaGlobeAsia,
  FaHandshake, FaIndustry, FaLandmark, FaLaptopCode, FaLightbulb,
  FaMapMarkerAlt, FaMoneyBillWave, FaPeopleArrows,
  FaPhone, FaPills, FaRocket, FaRss, FaSearch, FaSeedling, FaSlidersH,
  FaTag, FaTimes, FaUniversity, FaWhatsapp, FaSortAmountDown,
  FaRegBuilding, FaArrowUp, FaArrowRight,
} from 'react-icons/fa';
import {
  statesData,
  stateNames,
  defaultOverview,
  defaultInitiatives,
  defaultPriorities,
  getStateHod,
  defaultHod,
  getStateOffice,
  defaultOffice,
} from './statesData';
import CouncilLeadership from './CouncilLeadership.jsx';
import QueryForm from '../components/Sectors/QueryForm.jsx';

// ---- constants ----
const heroImage = 'https://images.pexels.com/photos/17585996/pexels-photo-17585996.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=627&w=1200';
const overviewDefaultImg = 'https://images.pexels.com/photos/3183197/pexels-photo-3183197.jpeg?auto=compress&cs=tinysrgb&w=1200';

// Event / publication image base (same convention as the Sector page).
const EVENT_IMAGE_BASE = 'https://assocham.org/assocham_backend/assets/images/';
const PUBLICATION_IMAGE_BASE = 'https://assocham.org/assocham_backend/assets/images/';

function resolveEventImage(image) {
  if (!image) return overviewDefaultImg;
  if (/^https?:\/\//i.test(image)) return image;
  return `${EVENT_IMAGE_BASE}${image}`;
}

function resolvePublicationImage(image) {
  if (!image) return overviewDefaultImg;
  if (/^https?:\/\//i.test(image)) return image;
  return `${PUBLICATION_IMAGE_BASE}${image}`;
}

// Detail-page path for a publication — SAME route as the Sector page.
function publicationPath(p) {
  if (!p) return null;
  const slug = p.slug || p.publication_slug || p.report_slug;
  return slug ? `/knowledge/reports/${slug}` : null;
}

// ---------------------------------------------------------------------------
// External link resolution for press releases AND press coverage.
//
// Coverage rows live in a different table than releases, so the URL column is
// rarely named the same. Instead of guessing column names forever, we:
//   1. try the well-known keys first (fast path, predictable ordering), then
//   2. fall back to scanning EVERY field for a real URL, skipping image/asset
//      fields so a banner jpg is never mistaken for the article link.
// Protocol-less values ("www.x.com/a") are repaired to https://.
// ---------------------------------------------------------------------------
const EXTERNAL_LINK_KEYS = [
  'read_more_link', 'readmore_link', 'read_more', 'readmore',
  'external_url', 'external_link', 'externallink',
  'news_link', 'newslink', 'news_url',
  'source_url', 'source_link', 'source',
  'media_link', 'media_url',
  'article_url', 'article_link',
  'coverage_link', 'coverage_url',
  'publication_link', 'publication_url',
  'press_link', 'press_url',
  'story_link', 'story_url',
  'reference_link', 'ref_link',
  'link', 'url', 'href', 'weblink', 'web_link', 'website',
];

// Fields that may hold absolute URLs but are NOT the article link.
const NON_LINK_KEY_RE =
  /(^|_)(img|image|images|thumb|thumbnail|photo|picture|banner|cover|icon|logo|file|pdf|attachment|doc|document|video|media_file|avatar)($|_)/i;

function cleanUrlValue(raw) {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== 'string' && typeof raw !== 'number') return null;

  const val = String(raw).trim();
  if (!val) return null;
  if (val === '#' || val === '-' || val === '--') return null;
  if (/^(null|undefined|n\/a|na|nil|false|true|0)$/i.test(val)) return null;

  if (/^https?:\/\//i.test(val)) return val;            // absolute
  if (val.startsWith('//')) return `https:${val}`;      // protocol-relative
  // Bare domain: "www.x.com/a", "economictimes.indiatimes.com/a"
  if (/^(www\.)?[a-z0-9-]+(\.[a-z0-9-]+)+(\/|\?|$)/i.test(val) && !val.startsWith('/')) {
    return `https://${val}`;
  }
  return null; // site-relative path -> internal, not external
}

function getExternalLink(item) {
  if (!item || typeof item !== 'object') return null;

  // Pass 1 — known keys, in priority order.
  for (const key of EXTERNAL_LINK_KEYS) {
    if (!(key in item)) continue;
    const url = cleanUrlValue(item[key]);
    if (url) return url;
  }

  // Pass 2 — scan every remaining field for a real URL.
  for (const [key, raw] of Object.entries(item)) {
    if (NON_LINK_KEY_RE.test(key)) continue;              // skip images/files
    if (/^(slug|title|date|excerpt|description|content|body|id)$/i.test(key)) continue;
    const url = cleanUrlValue(raw);
    // Only accept absolute-looking hits here to avoid false positives.
    if (url && /^https?:\/\//i.test(url)) return url;
  }

  return null;
}

const mandates = [
  { title: 'Policy Advocacy', text: 'Public policy, regulatory reforms and Ease of Doing Business inputs with Central and State Governments.', icon: FaLandmark },
  { title: 'Industry Competitiveness', text: 'Focused support for manufacturing, MSMEs, infrastructure, green growth and business competitiveness.', icon: FaChartLine },
  { title: 'Stakeholder Platform', text: 'Conferences, seminars, business forums and government-industry interactions across the state.', icon: FaPeopleArrows },
];

const services = [
  { label: 'Investment facilitation', icon: FaBuilding },
  { label: 'Certificate of Origin service', icon: FaCertificate },
  { label: 'Overseas delegation support', icon: FaGlobeAsia },
  { label: 'Sector-specific MoU facilitation', icon: FaFileSignature },
  { label: 'Industry academia connect', icon: FaUniversity },
  { label: 'Local chamber collaboration', icon: FaHandshake },
];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0 },
};

const HERO_CONTAINER = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};

const HERO_ITEM = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] } },
};

// sector icon mapping
const sectorIconRules = [
  [/agro|food|fisher/i, FaSeedling],
  [/renewable|energy|power/i, FaBolt],
  [/start-?up/i, FaRocket],
  [/intellectual|ipr/i, FaLightbulb],
  [/tax/i, FaMoneyBillWave],
  [/it\b|ites|e-commerce|digital|electronics|esdm/i, FaLaptopCode],
  [/msme|manufactur|mining|industr|engineering/i, FaIndustry],
  [/pharma|health|medical/i, FaPills],
  [/tourism|aviation|aerospace|hospitality/i, FaGlobeAsia],
  [/logistic|warehous|export/i, FaExchangeAlt],
];

function iconForSector(name) {
  const match = sectorIconRules.find(([regex]) => regex.test(name));
  return match ? match[1] : FaIndustry;
}

function toTitleCase(slug) {
  return slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// ---- date helpers ----
function getDateParts(dateStr = '') {
  const parts = dateStr.split(' ');
  const dayPart = parts[0] || '--';
  const restPart = parts.slice(1).join(' ');
  const monthPart = (restPart.split(',')[0] || '').split('-')[0].trim();
  const yearMatch = dateStr.match(/(20\d{2})/);
  const year = yearMatch ? yearMatch[1] : '2026';
  return { dayPart, monthPart, year };
}

function toDateObj(dateStr = '') {
  const { dayPart, monthPart, year } = getDateParts(dateStr);
  const cleanDay = dayPart.replace(/[^0-9]/g, '') || '1';
  const d = new Date(`${monthPart} ${cleanDay}, ${year}`);
  return isNaN(d.getTime()) ? null : d;
}

function isEventUpcoming(dateStr) {
  const d = toDateObj(dateStr);
  if (!d) return true;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d.getTime() >= today.getTime();
}

function toTimestamp(dateStr = '') {
  const d = toDateObj(dateStr);
  return d ? d.getTime() : 0;
}

// ---- contact helpers ----
function telHref(phone = '') {
  const digits = String(phone).replace(/[^\d+]/g, '');
  if (!digits) return '';
  return digits.startsWith('+') ? digits : `+91${digits.replace(/^0+/, '')}`;
}

function whatsappHref(phone = '') {
  const digits = String(phone).replace(/\D/g, '');
  if (!digits) return '';
  return digits.length === 10 ? `91${digits}` : digits;
}

function mapsUrl(office) {
  if (office?.mapUrl) return office.mapUrl;
  const q = office?.mapQuery || (office?.lines || []).join(' ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

// ---- default fallbacks ----
const defaultPublications = [];
const defaultPressReleases = [];
const defaultPressCoverage = [];

const defaultStateContent = {
  focusItems: ['Viksit Bharat 2047', 'Ease of Doing Business', 'Sustainable Development'],
  intro: 'A focused ASSOCHAM platform strengthening the state’s industrial ecosystem through policy advocacy, investment engagement, MSME growth, infrastructure dialogue, and sustainable development.',
  why: 'This council works closely with the Government and industry stakeholders to advance policy clarity, sector action and long-term competitiveness for the state. Detailed content for this council is coming soon.',
  overview: defaultOverview,
  sectors: [],
  leadership: [],
  initiatives: [],
  priorities: [],
  publications: [],
  pressReleases: [],
  pressCoverage: [],
  events: [],
};

const SERIF = {
  fontFamily: 'Fraunces, Georgia, "Times New Roman", serif',
};

// ---- Breadcrumb ----
function StateBreadcrumb({ stateLabel }) {
  return (
    <nav aria-label="Breadcrumb" className="absolute left-4 top-4 z-20 hidden sm:left-8 sm:top-6 sm:block">
      <ol className="flex flex-wrap items-center gap-2 rounded-full border border-white/15 bg-white/80 px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-[#141419] shadow-lg shadow-black/10 backdrop-blur-md">
        <li>
          <Link to="/" className="inline-flex items-center gap-1.5 transition-colors hover:text-[#DA2128]">
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 10.5 12 3l9 7.5" />
              <path d="M5 10v10h14V10" />
              <path d="M9 20v-6h6v6" />
            </svg>
            Home
          </Link>
        </li>
        <li className="text-black/30">&rsaquo;</li>
        <li>
          <Link to="/states" className="transition-colors hover:text-[#DA2128]">States</Link>
        </li>
        <li className="text-black/30">&rsaquo;</li>
        <li className="rounded-full bg-[#DA2128] px-3 py-1 text-white">{stateLabel}</li>
      </ol>
    </nav>
  );
}

// ========================== MAIN COMPONENT ==========================
export default function StatePage() {
  // ---- Route slug ----
  const { stateSlug } = useParams();
  const location = useLocation();
  const slug = stateSlug || location.pathname.replace(/^\//, '');
  const data = statesData[slug] || defaultStateContent;

  const name = data.name || `${stateNames[slug] || toTitleCase(slug)} State Development Council`;
  const stateLabel = stateNames[slug] || toTitleCase(slug || 'State');
  const overviewParagraphs = data.overview && data.overview.length > 0 ? data.overview : defaultOverview;
  const initiatives = data.initiatives && data.initiatives.length > 0 ? data.initiatives : defaultInitiatives;
  const priorities = data.priorities && data.priorities.length > 0 ? data.priorities : defaultPriorities;

  // ---- State HoD & Office (static data, single source of truth) ----
  // hod.title comes from statesData.js -> 'ASSOCHAM Regional Head' for
  // Bharti Sood / region desks, 'ASSOCHAM State Head' for everyone else,
  // and 'Council Secretariat' when nothing is mapped yet.
  const hod = useMemo(() => getStateHod(slug), [slug]);
  const hodRoleLabel = hod?.title || defaultHod.title;
  const hodTitleSuffix = hodRoleLabel.replace(/^ASSOCHAM\s+/i, ''); // 'Regional Head' / 'State Head'
  const hasNamedHod = !hod?.isFallback && Boolean(hod?.name && hod.name !== defaultHod.name);
  const hodSubtitle = hasNamedHod
    ? `${hodTitleSuffix} — ${name}`
    : `Central Secretariat — ${stateLabel || 'ASSOCHAM'}`;
  const hodEmail = hod?.email || defaultHod.email;
  const hodPhone = hod?.phone || defaultHod.phone;
  const hodPhoneHref = telHref(hodPhone);
  const hodWaNumber = whatsappHref(hod?.whatsapp || hodPhone);

  const office = useMemo(() => getStateOffice(slug), [slug]);
  const officeEmail = office.email || defaultOffice.email;
  const officePhone = office.phone || defaultOffice.phone;
  const officePhoneHref = office.phoneHref || `tel:${telHref(officePhone)}`;

  // ---- UI state (events, press, scroll) ----
  const [eventTab, setEventTab] = useState('all');
  const [eventKeyword, setEventKeyword] = useState('');
  const [eventTag, setEventTag] = useState('');
  const [eventPlace, setEventPlace] = useState('');
  const [eventAnimKey, setEventAnimKey] = useState(0);
  const [eventsVisible, setEventsVisible] = useState(2);
  const EVENTS_STEP = 2;

  const [pressTab, setPressTab] = useState('releases');
  const [pressKeyword, setPressKeyword] = useState('');
  const [pressSort, setPressSort] = useState('newest');
  const [pressPage, setPressPage] = useState(1);
  const pressResultsRef = useRef(null);
  const PRESS_PAGE_SIZE = 5;

  const [submitted, setSubmitted] = useState(false);
  const [progress, setProgress] = useState(0);

  // ---- Data from API ----
  const [regionData, setRegionData] = useState(null);
  const [leadershipData, setLeadershipData] = useState([]);
  const [eventsData, setEventsData] = useState([]);
  const [publicationsData, setPublicationsData] = useState([]);
  const [pressReleasesData, setPressReleasesData] = useState([]);
  const [pressCoverageData, setPressCoverageData] = useState([]);

  // ---- Fetch region, leadership, events and publications (parallel, fault tolerant) ----
  useEffect(() => {
    let active = true;

    async function fetchData() {
      // reset so stale data from a previous council never shows
      setRegionData(null);
      setLeadershipData([]);
      setEventsData([]);
      setPublicationsData([]);
      setPressReleasesData([]);
      setPressCoverageData([]);

      const results = await Promise.allSettled([
        getRegionBySlug(slug),
        getCouncilLeadership(slug),
        fetchRegionEvents(slug),
        fetchRegionPublications(slug),
        fetchRegionPressReleases(slug),
        fetchRegionPressCoverage(slug),
      ]);

      if (!active) return;

      const [regionRes, leadersRes, eventsRes, pubsRes, pressRelRes, pressCovRes] = results;

      if (regionRes.status === 'fulfilled' && regionRes.value) setRegionData(regionRes.value);
      if (leadersRes.status === 'fulfilled' && Array.isArray(leadersRes.value)) setLeadershipData(leadersRes.value);
      if (eventsRes.status === 'fulfilled' && Array.isArray(eventsRes.value)) setEventsData(eventsRes.value);
      if (pubsRes.status === 'fulfilled' && Array.isArray(pubsRes.value)) setPublicationsData(pubsRes.value);
      if (pressRelRes.status === 'fulfilled' && Array.isArray(pressRelRes.value)) setPressReleasesData(pressRelRes.value);
      if (pressCovRes.status === 'fulfilled' && Array.isArray(pressCovRes.value)) setPressCoverageData(pressCovRes.value);

      // TEMP DEBUG — shows the real column names coming from the API.
      // Delete these two lines once external links are confirmed working.
      if (pressRelRes.status === 'fulfilled') console.log('[press releases] first row:', pressRelRes.value?.[0]);
      if (pressCovRes.status === 'fulfilled') console.log('[press coverage] first row:', pressCovRes.value?.[0]);
    }

    fetchData();
    return () => { active = false; };
  }, [slug]);

  // ---- Final lists: API first, static fallback ----
  const finalLeadership = leadershipData.length > 0 ? leadershipData : (data.leadership || []);
  const EVENTS = (eventsData && eventsData.length > 0) ? eventsData : (data.events || []);

  const PUBLICATIONS = (publicationsData && publicationsData.length > 0)
    ? publicationsData
    : (data.publications || defaultPublications);

  const PRESS_RELEASES = (pressReleasesData && pressReleasesData.length > 0)
    ? pressReleasesData
    : (data.pressReleases || defaultPressReleases);

  const PRESS_COVERAGE = (pressCoverageData && pressCoverageData.length > 0)
    ? pressCoverageData
    : (data.pressCoverage || defaultPressCoverage);

  // ---- Scroll progress ----
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(h > 0 ? (window.scrollY / h) * 100 : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToOverview = (e) => {
    e?.preventDefault();
    document.getElementById('overview')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ---- Press logic ----
  const pressSource = pressTab === 'releases' ? PRESS_RELEASES : PRESS_COVERAGE;
  const pressFiltered = useMemo(() => {
    const kw = pressKeyword.trim().toLowerCase();
    let list = pressSource.filter((item) => {
      if (!kw) return true;
      const hay = `${item.title} ${item.excerpt || ''}`.toLowerCase();
      return hay.includes(kw);
    });
    list = [...list].sort((a, b) => {
      const ta = toTimestamp(a.date);
      const tb = toTimestamp(b.date);
      return pressSort === 'newest' ? tb - ta : ta - tb;
    });
    return list;
  }, [pressSource, pressKeyword, pressSort]);

  useEffect(() => setPressPage(1), [pressTab, pressKeyword, pressSort]);

  const pressTotalPages = Math.max(1, Math.ceil(pressFiltered.length / PRESS_PAGE_SIZE));
  const pressClampedPage = Math.min(pressPage, pressTotalPages);
  const pressStart = (pressClampedPage - 1) * PRESS_PAGE_SIZE;
  const pressPageItems = pressFiltered.slice(pressStart, pressStart + PRESS_PAGE_SIZE);

  const scrollToPressResults = () =>
    pressResultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const changePressPage = (n) => {
    setPressPage(n);
    scrollToPressResults();
  };

  // ---- Hero image & overview ----
  const heroImageFinal = regionData?.header_banner_img || heroImage;
  const overviewImageFinal = regionData?.image || data.overviewImage || overviewDefaultImg;
  const overviewTitleFinal = regionData?.title || name;

  // ---- Events filters ----
  const eventTags = useMemo(
    () => Array.from(new Set(EVENTS.map((e) => e.tag).filter(Boolean))),
    [EVENTS]
  );
  const eventPlaces = useMemo(
    () => Array.from(new Set(EVENTS.map((e) => e.place).filter(Boolean))),
    [EVENTS]
  );
  const filteredEvents = useMemo(() => {
    const kw = eventKeyword.trim().toLowerCase();
    return EVENTS.filter((e) => {
      const upcoming = isEventUpcoming(e.date);
      if (eventTab === 'forthcoming' && !upcoming) return false;
      if (eventTab === 'past' && upcoming) return false;
      if (eventTag && e.tag !== eventTag) return false;
      if (eventPlace && e.place !== eventPlace) return false;
      if (kw) {
        const hay = `${e.title} ${e.text} ${e.tag || ''} ${e.place || ''}`.toLowerCase();
        if (!hay.includes(kw)) return false;
      }
      return true;
    });
  }, [eventTab, eventTag, eventPlace, eventKeyword, EVENTS]);

  useEffect(() => { setEventAnimKey((k) => k + 1); }, [eventTab, eventTag, eventPlace]);
  useEffect(() => { setEventsVisible(2); }, [eventTab, eventTag, eventPlace, eventKeyword]);

  const clearEventFilters = () => {
    setEventKeyword('');
    setEventTag('');
    setEventPlace('');
    setEventTab('all');
    setEventAnimKey((k) => k + 1);
  };

  const visibleEvents = filteredEvents.slice(0, eventsVisible);
  const hasMoreEvents = eventsVisible < filteredEvents.length;
  const loadMoreEvents = () =>
    setEventsVisible((v) => Math.min(v + EVENTS_STEP, filteredEvents.length));

  // ---- Contact form ----
  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    window.setTimeout(() => setSubmitted(false), 5000);
  };

  const sectionEyebrow = (label, color = '#DB6400') => (
    <p className="text-xs font-semibold uppercase tracking-[0.35em]" style={{ color }}>
      {label}
    </p>
  );

  // ---- Render ----
  return (
    <main className="min-h-screen overflow-hidden bg-[#FAF7F2] text-[#241A16] selection:bg-[#DA2128] selection:text-white">
      <style>{`
        html { scroll-behavior: smooth; }
        ::selection { background:#DA2128; color:#fff; }
        ::-webkit-scrollbar { width:10px; height:10px; }
        ::-webkit-scrollbar-track { background:#FEF7F4; }
        ::-webkit-scrollbar-thumb { background:linear-gradient(#DA2128,#DB6400); border-radius:9999px; }
        .event-fade-up { animation: st-fade-up 0.6s cubic-bezier(.2,.8,.2,1) both; }
        @keyframes st-fade-up { 0% { opacity: 0; transform: translateY(22px); } 100% { opacity: 1; transform: translateY(0); } }
        @keyframes st-float { 0%,100%{transform:translateY(0) rotate(0)} 50%{transform:translateY(-16px) rotate(3deg)} }
        @keyframes st-float-slow { 0%,100%{transform:translate(0,0)} 50%{transform:translate(10px,-22px)} }
        @keyframes st-blob { 0%,100%{border-radius:62% 38% 46% 54% / 55% 48% 52% 45%} 50%{border-radius:42% 58% 66% 34% / 38% 62% 38% 62%} }
        @keyframes st-pulse-ring { 0%{transform:scale(.85);opacity:.7} 70%{transform:scale(1.5);opacity:0} 100%{opacity:0} }
        @keyframes st-kenburns { 0%{transform:scale(1.02) translate(0,0)} 100%{transform:scale(1.16) translate(1.2%,-1.2%)} }
        @keyframes st-scroll-dot { 0%{transform:translateY(0);opacity:.9} 70%{transform:translateY(14px);opacity:0} 100%{transform:translateY(0);opacity:0} }
        @keyframes st-pub-slide { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
        @keyframes st-shine { 0%{transform:translateX(-120%) skewX(-20deg)} 60%,100%{transform:translateX(240%) skewX(-20deg)} }
        .anim-st-float{animation:st-float 7s ease-in-out infinite}
        .anim-st-float-slow{animation:st-float-slow 11s ease-in-out infinite}
        .anim-st-blob{animation:st-blob 14s ease-in-out infinite}
        .anim-st-pulse-ring{animation:st-pulse-ring 2.6s cubic-bezier(.22,.61,.36,1) infinite}
        .anim-st-kenburns{animation:st-kenburns 26s ease-in-out infinite alternate;will-change:transform}
        .anim-st-scroll-dot{animation:st-scroll-dot 1.8s cubic-bezier(.45,.05,.55,.95) infinite}
        .st-pub-card-bg{background:linear-gradient(120deg,#DA2128 0%,#C81E24 18%,#DB6400 42%,#F59C00 62%,#DB6400 82%,#DA2128 100%);background-size:300% 100%;animation:st-pub-slide 9s ease-in-out infinite}
        .st-shine-parent{position:relative;overflow:hidden}
        .st-shine-parent::after{content:"";position:absolute;top:0;bottom:0;left:0;width:45%;background:linear-gradient(90deg,transparent,rgba(255,255,255,.45),transparent);transform:translateX(-120%) skewX(-20deg)}
        .st-shine-parent:hover::after{animation:st-shine 1s ease-out}
        .font-heading { font-family: 'Fraunces', Georgia, serif; }
        .sector-section { scroll-margin-top: 2rem; }
        .st-clamp-2 { display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
        @media (prefers-reduced-motion: reduce){*,*::before,*::after{animation-duration:.001ms!important;animation-iteration-count:1!important;transition-duration:.001ms!important}}
      `}</style>

      {/* Scroll progress bar */}
      <div className="fixed inset-x-0 top-0 z-[60] h-1">
        <div
          className="h-full bg-gradient-to-r from-[#DA2128] via-[#DB6400] to-[#F59C00] transition-[width] duration-150"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Back to top */}
      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Back to top"
        className={`fixed bottom-7 right-7 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-[#DA2128] text-white shadow-xl shadow-[#DA2128]/35 transition-all duration-300 hover:-translate-y-1 hover:bg-[#DB6400] ${
          progress > 8 ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-6 opacity-0'
        }`}
      >
        <FaArrowUp className="h-4 w-4" />
      </button>

      {/* ==================== HERO ==================== */}
      <section id="top" className="relative isolate flex min-h-[70vh] flex-col overflow-hidden bg-stone-900">
        <div className="absolute inset-0">
          <img
            src={heroImageFinal}
            alt={`${name} banner`}
            className="anim-st-kenburns h-full w-full object-cover brightness-[1.25] saturate-[1.05]"
          />
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_45%,rgba(0,0,0,0.45),transparent_75%)]" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/30" />
        </div>

        <motion.div
          aria-hidden="true"
          className="pointer-events-none absolute -right-32 -top-40 h-[26rem] w-[26rem] rounded-full border-[40px] border-[#F59C00]/20"
          animate={{ rotate: 360 }}
          transition={{ duration: 35, repeat: Infinity, ease: 'linear' }}
        />
        <div className="anim-st-blob anim-st-float pointer-events-none absolute -left-24 bottom-10 h-72 w-72 bg-[#DA2128]/25 blur-3xl" />
        <div className="anim-st-blob anim-st-float-slow pointer-events-none absolute right-[12%] top-[18%] h-64 w-64 bg-[#F59C00]/20 blur-3xl" />
        <div className="anim-st-pulse-ring pointer-events-none absolute right-[16%] top-[30%] h-24 w-24 rounded-full border border-white/20" />
        <div className="anim-st-pulse-ring pointer-events-none absolute left-[11%] top-[42%] h-14 w-14 rounded-full border border-[#F59C00]/40" />

        <StateBreadcrumb stateLabel={stateLabel} />

        <motion.div
          variants={HERO_CONTAINER}
          initial="hidden"
          animate="show"
          className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col items-center justify-center px-6 py-24 text-center"
        >
          <motion.div variants={HERO_ITEM}>
            <span className="inline-flex items-center gap-2.5 rounded-full border border-white/25 bg-white/10 px-5 py-2 text-[10.5px] font-bold uppercase tracking-[0.28em] text-white shadow-lg backdrop-blur-md">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#F59C00] opacity-80" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#F59C00]" />
              </span>
              Industry Policy Forum
            </span>
          </motion.div>

          <motion.h1
            variants={HERO_ITEM}
            style={SERIF}
            className="mt-7 max-w-4xl text-3xl font-medium leading-[1.12] tracking-tight text-white drop-shadow-[0_4px_24px_rgba(0,0,0,0.35)] md:text-5xl lg:text-6xl"
          >
            {overviewTitleFinal}
          </motion.h1>

          <motion.div variants={HERO_ITEM} className="mt-8 flex items-center justify-center gap-3">
            <span className="h-px w-16 bg-gradient-to-r from-transparent to-white/70" />
            <motion.span
              animate={{ rotate: 360 }}
              transition={{ duration: 14, repeat: Infinity, ease: 'linear' }}
              className="h-2.5 w-2.5 rotate-45 bg-gradient-to-br from-[#F59C00] to-[#DA2128]"
            />
            <span className="h-px w-16 bg-gradient-to-l from-transparent to-white/70" />
          </motion.div>

          <motion.div variants={HERO_ITEM} className="mt-12">
            <button
              type="button"
              onClick={scrollToOverview}
              aria-label="Scroll to overview"
              className="flex flex-col items-center gap-1.5 text-white/60 transition-colors duration-300 hover:text-white"
            >
              <span className="text-[9px] font-extrabold uppercase tracking-[0.34em]">Scroll</span>
              <span className="flex h-9 w-[22px] items-start justify-center rounded-full border-2 border-white/40 p-1">
                <span className="anim-st-scroll-dot h-2 w-[5px] rounded-full bg-gradient-to-b from-[#F59C00] to-[#DA2128]" />
              </span>
            </button>
          </motion.div>
        </motion.div>
      </section>

      {/* ==================== OVERVIEW ==================== */}
      <section
        id="overview"
        className="sector-section relative z-10 -mt-10 scroll-mt-6 rounded-t-[44px] bg-white py-16 lg:py-24"
      >
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mb-8 w-full lg:float-left lg:mb-8 lg:mr-10 lg:w-[340px]"
          >
            <div className="relative">
              <div className="overflow-hidden rounded-[28px] border-4 border-white bg-white shadow-2xl shadow-stone-900/15">
                <img
                  src={overviewImageFinal}
                  alt={name}
                  className="h-[360px] w-full object-cover transition-transform duration-[1400ms] hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="anim-st-float absolute -bottom-6 -right-3 w-56 rounded-2xl border border-stone-100 bg-white p-4 shadow-2xl">
                <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#DA2128]">State Council</p>
                <p className="mt-1 text-[14.5px] font-bold leading-snug text-stone-900" style={SERIF}>{name}</p>
              </div>
              <div className="anim-st-float-slow absolute -left-5 -top-6 hidden w-32 overflow-hidden rounded-2xl border-4 border-white shadow-xl sm:block">
                {/* <div className="st-pub-card-bg flex h-20 w-full items-center justify-center text-white">
                  <FaIndustry className="h-6 w-6 opacity-90" />
                </div> */}
              </div>
            </div>
          </motion.div>

          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-[#DB6400]">Overview</p>
          <h2 className="mt-3 text-2xl font-medium leading-[1.25] tracking-tight text-[#241A16] md:text-4xl" style={SERIF}>
            <span className="text-[#DA2128]">{name.replace(/State (Development )?Council/i, '').trim()}</span>
          </h2>

          <div className="mt-6 space-y-4 border-l-2 border-[#DA2128]/20 pl-5">
            {regionData?.long_description ? (
              <div
                className="prose prose-stone max-w-none text-[15.5px] leading-relaxed text-stone-600 [&_p]:mb-4"
                dangerouslySetInnerHTML={{ __html: regionData.long_description }}
              />
            ) : (
              overviewParagraphs.map((p, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, x: 15 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                  className="text-[15.5px] leading-relaxed text-stone-600"
                >
                  {i === 0 && p.includes('ASSOCHAM') ? (
                    <>
                      <span className="font-bold text-stone-900">ASSOCHAM</span>
                      {p.replace('ASSOCHAM', '')}
                    </>
                  ) : (
                    p
                  )}
                </motion.p>
              ))
            )}
          </div>

          {data.sectors && data.sectors.length > 0 && (
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {data.sectors.slice(0, 6).map((sectorName, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2.5 rounded-2xl bg-[#FAF7F2] p-3.5 ring-1 ring-stone-200 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#DA2128]/5 hover:ring-[#DA2128]/30"
                >
                  <FaCheck className="mt-1 h-3.5 w-3.5 shrink-0 text-[#DA2128]" />
                  <p className="text-[13px] font-medium leading-relaxed text-stone-700">{sectorName}</p>
                </div>
              ))}
            </div>
          )}
          <div className="clear-both" />
        </div>
      </section>

      {/* ==================== COUNCIL LEADERSHIP ==================== */}
      <section id="councils" className="sector-section relative scroll-mt-6 overflow-hidden bg-white py-20 lg:py-24">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#FAF7F2] to-transparent" />
        <div className="anim-st-blob anim-st-float-slow pointer-events-none absolute -right-32 bottom-0 h-80 w-80 bg-[#F59C00]/10 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
          <div className="mb-2">
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-[#DB6400]">Councils</p>
            <h2 className="mt-3 text-2xl font-medium leading-[1.25] tracking-tight text-[#241A16] md:text-4xl" style={SERIF}>
              Council Leadership
            </h2>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <CouncilLeadership members={finalLeadership} councilName={name} />
          </motion.div>
        </div>
      </section>

      {/* ==================== SECTORS ==================== */}
      {/* {data.sectors && data.sectors.length > 0 && (
        <section id="sectors" className="relative bg-[#FEF7F4] py-16 lg:py-20">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <div className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.35em] text-[#DB6400]">Sectoral Engine</p>
                <h2 className="mt-3 text-2xl font-medium leading-[1.25] tracking-tight text-[#241A16] md:text-4xl" style={SERIF}>
                  Focused committees for focused outcomes.
                </h2>
              </div>
              <p className="max-w-md text-sm leading-relaxed text-[#241A16]/75 font-normal">
                Sector sub-committees help convert member inputs into state-specific policy recommendations,
                promotion opportunities and business interactions.
              </p>
            </div>
            <div className="grid border-t border-l border-[#241A16]/10 sm:grid-cols-2 lg:grid-cols-4">
              {data.sectors.map((sectorName, index) => {
                const Icon = iconForSector(sectorName);
                return (
                  <motion.div
                    key={sectorName}
                    className="group relative flex min-h-[160px] flex-col justify-between border-r border-b border-[#241A16]/10 bg-white/60 p-6 transition-all duration-300 hover:bg-[#DA2128]"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.04 }}
                  >
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#DA2128]/10 text-[#DA2128] transition-all duration-300 group-hover:bg-white/20 group-hover:text-white group-hover:scale-105">
                      <Icon className="text-xl" />
                    </div>
                    <p
                      className="mt-6 text-base font-medium tracking-tight text-[#241A16] transition-colors duration-300 group-hover:text-white"
                      style={SERIF}
                    >
                      {sectorName}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )} */}

      {/* ==================== EVENTS ==================== */}
      <section id="events" className="relative scroll-mt-20 overflow-hidden bg-white py-16 lg:py-20">
        <div className="pointer-events-none absolute -right-24 top-10 h-72 w-72 rounded-full bg-[#F59C00]/10 blur-3xl" />
        <div className="pointer-events-none absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-[#DA2128]/10 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
          <div className="mb-10 max-w-2xl">
            {sectionEyebrow('Engagements & Events', '#DB6400')}
            <h2 className="mt-3 text-2xl font-medium leading-[1.25] tracking-tight text-[#241A16] md:text-4xl" style={SERIF}>
              Recent &amp; Upcoming Initiatives
            </h2>
          </div>
          <div className="grid gap-6 lg:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)]">
            {/* Sidebar */}
            <motion.aside
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-[26px] border border-[#DA2128]/10 bg-[#FAF7F2] p-5 shadow-[0_18px_44px_rgba(218,33,40,0.08)] lg:sticky lg:top-24 lg:h-fit"
            >
              <div className="mb-5 flex items-center justify-between border-b border-[#DA2128]/10 pb-3.5">
                <div>
                  <p className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-[#DB6400]">Filter by</p>
                  <h3 className="mt-1 text-xl font-bold text-stone-900" style={SERIF}>Events Search</h3>
                </div>
                <div className="rounded-xl bg-gradient-to-br from-[#FFE3DA] to-[#FFE7CF] p-2 text-[#DA2128]">
                  <FaSlidersH className="h-4 w-4" />
                </div>
              </div>
              <div className="space-y-3.5">
                <div className="space-y-1.5">
                  <label className="text-[0.8rem] font-bold text-stone-700">Keywords</label>
                  <div className="relative">
                    <FaSearch className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#DA2128]" />
                    <input
                      type="text"
                      value={eventKeyword}
                      onChange={(e) => setEventKeyword(e.target.value)}
                      placeholder="Search by title, place..."
                      className="w-full rounded-xl border border-[#DA2128]/10 bg-white py-2.5 pl-10 pr-3.5 text-[0.8rem] font-medium text-stone-700 transition duration-300 placeholder:text-stone-400 hover:border-[#DA2128]/30 focus:border-[#DA2128] focus:outline-none focus:ring-2 focus:ring-[#DA2128]/10"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[0.8rem] font-bold text-stone-700">Status</label>
                  <div className="flex gap-1.5 rounded-xl border border-[#DA2128]/10 bg-white p-1">
                    {[
                      ['all', 'All'],
                      ['forthcoming', 'Upcoming'],
                      ['past', 'Past'],
                    ].map(([v, l]) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setEventTab(v)}
                        className={`flex-1 rounded-lg px-2 py-2 text-[0.75rem] font-bold transition duration-300 ${
                          eventTab === v
                            ? 'bg-gradient-to-r from-[#DA2128] to-[#F59C00] text-white shadow-md'
                            : 'text-stone-500 hover:bg-[#FAF7F2]'
                        }`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                {eventTags.length > 0 && (
                  <div className="space-y-1.5">
                    <label className="text-[0.8rem] font-bold text-stone-700">Category</label>
                    <div className="relative">
                      <FaTag className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#DA2128]" />
                      <select
                        value={eventTag}
                        onChange={(e) => setEventTag(e.target.value)}
                        className="w-full appearance-none rounded-xl border border-[#DA2128]/10 bg-white py-2.5 pl-10 pr-9 text-[0.8rem] font-medium text-stone-700 transition duration-300 hover:border-[#DA2128]/30 focus:border-[#DA2128] focus:outline-none focus:ring-2 focus:ring-[#DA2128]/10"
                      >
                        <option value="">All categories</option>
                        {eventTags.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                      <svg viewBox="0 0 24 24" className="pointer-events-none absolute right-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-stone-400" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </div>
                  </div>
                )}
                {eventPlaces.length > 0 && (
                  <div className="space-y-1.5">
                    <label className="text-[0.8rem] font-bold text-stone-700">Location</label>
                    <div className="relative">
                      <FaMapMarkerAlt className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#DA2128]" />
                      <select
                        value={eventPlace}
                        onChange={(e) => setEventPlace(e.target.value)}
                        className="w-full appearance-none rounded-xl border border-[#DA2128]/10 bg-white py-2.5 pl-10 pr-9 text-[0.8rem] font-medium text-stone-700 transition duration-300 hover:border-[#DA2128]/30 focus:border-[#DA2128] focus:outline-none focus:ring-2 focus:ring-[#DA2128]/10"
                      >
                        <option value="">All locations</option>
                        {eventPlaces.map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                      <svg viewBox="0 0 24 24" className="pointer-events-none absolute right-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-stone-400" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={clearEventFilters}
                className="mt-5 inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-[#DA2128]/20 bg-white px-4 py-2.5 text-[0.8rem] font-bold text-[#DA2128] transition duration-300 hover:bg-[#FEF7F4]"
              >
                <FaTimes className="h-3.5 w-3.5" />
                Clear filters
              </button>
            </motion.aside>

            {/* Main list */}
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3 pb-5">
                <span className="text-[12.5px] font-semibold text-stone-500">
                  {filteredEvents.length === 0
                    ? EVENTS.length === 0
                      ? 'No events published yet for this council'
                      : 'No events match your filters'
                    : `Showing ${Math.min(eventsVisible, filteredEvents.length)} of ${filteredEvents.length} event${
                        filteredEvents.length > 1 ? 's' : ''
                      }`}
                </span>
              </div>
              <div key={eventAnimKey} className="grid gap-6">
                {filteredEvents.length === 0 ? (
                  <div className="rounded-[24px] border border-dashed border-[#DA2128]/25 bg-[#FAF7F2] p-10 text-center">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#DA2128] to-[#F59C00] text-white shadow-lg shadow-[#DA2128]/25">
                      <FaSearch className="h-6 w-6" />
                    </div>
                    <h4 className="mt-4 text-lg font-bold text-stone-900" style={SERIF}>
                      {EVENTS.length === 0 ? 'No engagements published yet' : 'No matching events found'}
                    </h4>
                    <p className="mx-auto mt-2 max-w-sm text-[13px] leading-relaxed text-stone-500">
                      {EVENTS.length === 0
                        ? "Check back soon for this council's upcoming and past engagements."
                        : 'Try clearing a filter or switching the status tab to see more initiatives.'}
                    </p>
                    {EVENTS.length > 0 && (
                      <button
                        type="button"
                        onClick={clearEventFilters}
                        className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#DA2128] to-[#F59C00] px-5 py-2.5 text-[12.5px] font-bold text-white shadow-lg shadow-[#DA2128]/25 transition hover:shadow-[#DA2128]/40"
                      >
                        Reset all filters
                      </button>
                    )}
                  </div>
                ) : (
                  visibleEvents.map((event, i) => (
                    <StateEventCard key={`${event.title}-${eventAnimKey}-${i}`} event={event} index={i} />
                  ))
                )}
              </div>
              {hasMoreEvents && (
                <div className="mt-8 text-center">
                  <button
                    type="button"
                    onClick={loadMoreEvents}
                    className="st-shine-parent inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#DA2128] to-[#F59C00] px-8 py-3 text-[13px] font-bold text-white shadow-lg shadow-[#DA2128]/30 transition-all hover:-translate-y-0.5 hover:shadow-[#DA2128]/45"
                  >
                    Load More Events
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ==================== PRESS RELEASES & COVERAGE ==================== */}
      <section
        id="press"
        className="relative z-10 -mt-10 scroll-mt-20 overflow-hidden rounded-t-[44px] bg-[#FAF7F2] py-16 lg:py-20"
      >
        <div className="pointer-events-none absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-[#DA2128]/10 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
          <div className="mb-10 max-w-2xl">
            {sectionEyebrow('Newsroom', '#DB6400')}
            <h2 className="mt-3 text-2xl font-medium leading-[1.25] tracking-tight text-[#241A16] md:text-4xl" style={SERIF}>
              Press Release &amp; <span className="text-[#DA2128]">Press Coverage</span>
            </h2>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-2xl border border-stone-200 bg-white p-4 shadow-[0_10px_40px_-24px_rgba(218,33,40,0.35)] sm:p-5"
          >
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="inline-flex w-fit rounded-full bg-stone-100 p-1">
                {[
                  { key: 'releases', label: 'Press Releases', count: PRESS_RELEASES.length },
                  { key: 'coverage', label: 'Press Coverage', count: PRESS_COVERAGE.length },
                ].map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setPressTab(t.key)}
                    className={`relative rounded-full px-5 py-2.5 text-[12.5px] font-bold uppercase tracking-[0.06em] transition-all duration-300 ${
                      pressTab === t.key ? 'bg-white text-[#DA2128] shadow-md' : 'text-stone-500 hover:text-stone-700'
                    }`}
                  >
                    {t.label}
                    <span
                      className={`ml-2 rounded-full px-2 py-0.5 text-[10px] ${
                        pressTab === t.key ? 'bg-[#DA2128]/10 text-[#DA2128]' : 'bg-stone-200 text-stone-500'
                      }`}
                    >
                      {t.count}
                    </span>
                  </button>
                ))}
              </div>
              <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center lg:max-w-xl">
                <div className="relative flex-1">
                  <FaSearch className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#DA2128]" />
                  <input
                    type="text"
                    value={pressKeyword}
                    onChange={(e) => setPressKeyword(e.target.value)}
                    placeholder="Search press releases & coverage..."
                    className="w-full rounded-xl border border-stone-200 bg-[#FAF7F2] py-2.5 pl-10 pr-3.5 text-[13px] font-medium text-stone-700 transition duration-300 placeholder:text-stone-400 hover:border-[#DA2128]/30 focus:border-[#DA2128] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#DA2128]/10"
                  />
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <FaSortAmountDown className="h-3.5 w-3.5 text-stone-400" />
                  <div className="flex overflow-hidden rounded-xl text-[11.5px] font-bold ring-1 ring-stone-200">
                    {[
                      ['newest', 'Newest'],
                      ['oldest', 'Oldest'],
                    ].map(([v, l]) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setPressSort(v)}
                        className={`px-3.5 py-2.5 transition-colors ${
                          pressSort === v
                            ? 'bg-gradient-to-r from-[#DA2128] to-[#DB6400] text-white'
                            : 'bg-white text-stone-500 hover:text-[#DA2128]'
                        }`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Active filter chip */}
            <AnimatePresence>
              {pressKeyword.trim() && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="flex flex-wrap items-center gap-2 pt-4">
                    <span className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-stone-400">
                      Active filter —
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-[#DA2128]/25 bg-[#FEF7F4] py-1 pl-3 pr-1.5 text-[11.5px] font-bold text-[#DA2128]">
                      "{pressKeyword.trim()}"
                      <button
                        type="button"
                        onClick={() => setPressKeyword('')}
                        className="grid h-[18px] w-[18px] place-items-center rounded-full bg-[#DA2128]/10 text-[#DA2128] transition-colors hover:bg-[#DA2128] hover:text-white"
                        aria-label="Clear press search"
                      >
                        <FaTimes className="h-2 w-2" />
                      </button>
                    </span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          <div ref={pressResultsRef} className="mt-10 scroll-mt-32">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-stone-900/90 pb-4">
              <h3 className="flex items-center gap-2.5 text-xl font-bold text-stone-900 sm:text-2xl" style={SERIF}>
                {pressTab === 'releases' ? 'Press Releases' : 'Press Coverage'}
                <button
                  type="button"
                  title="Subscribe via RSS"
                  className="text-[#DA2128] transition-colors hover:text-[#DB6400]"
                  onClick={(e) => e.preventDefault()}
                >
                  <FaRss className="h-4 w-4" />
                </button>
              </h3>
              <span className="text-[12.5px] font-semibold text-stone-400">
                {pressFiltered.length === 0
                  ? 'No matching results'
                  : `Showing ${pressStart + 1}–${Math.min(pressStart + PRESS_PAGE_SIZE, pressFiltered.length)} of ${pressFiltered.length}`}
              </span>
            </div>

            {pressPageItems.length > 0 ? (
              <AnimatePresence mode="popLayout">
                {pressPageItems.map((item, i) => {
                  // External link wins; otherwise BOTH tabs use the same internal route.
                  const externalLink = getExternalLink(item);
                  const internalPath = item.slug ? `/press-release/${item.slug}` : null;
                  const targetUrl = externalLink || internalPath;
                  const isExternal = Boolean(externalLink);

                  const LinkWrapper = ({ children, className, style }) => {
                    if (isExternal) {
                      return (
                        <a
                          href={targetUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={className}
                          style={style}
                        >
                          {children}
                        </a>
                      );
                    }
                    if (internalPath) {
                      return (
                        <Link to={internalPath} state={{ item }} className={className} style={style}>
                          {children}
                        </Link>
                      );
                    }
                    return (
                      <span className={className} style={style}>
                        {children}
                      </span>
                    );
                  };

                  return (
                    <motion.article
                      key={`${pressTab}-${item.title}-${i}`}
                      layout
                      initial={{ opacity: 0, y: 18 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -12 }}
                      transition={{ duration: 0.35, delay: i * 0.05 }}
                      className="group flex flex-col gap-4 border-b border-stone-200 py-6 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2.5">
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FEF7F4] px-2.5 py-1 text-[10.5px] font-extrabold uppercase tracking-[0.14em] text-[#DA2128]">
                            <FaCalendarAlt className="h-3 w-3" />
                            {item.date}
                          </span>
                          <span className="inline-flex rounded-full border border-stone-200 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.1em] text-stone-500">
                            {pressTab === 'releases' ? 'Release' : 'Coverage'}
                          </span>
                          {isExternal && (
                            <span className="inline-flex items-center gap-1 rounded-full border border-[#DB6400]/30 bg-[#FFF8EC] px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.1em] text-[#B45309]">
                              External
                            </span>
                          )}
                        </div>

                        <LinkWrapper
                          className="mt-3 block text-[17px] font-bold leading-snug text-stone-900 transition-colors hover:text-[#DA2128] sm:text-[18px]"
                          style={SERIF}
                        >
                          {item.title}
                        </LinkWrapper>

                        <p className="mt-2 text-[13.5px] leading-relaxed text-stone-600">{item.excerpt}</p>
                      </div>

                      <LinkWrapper className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full border border-[#DA2128]/25 px-4 py-2 text-[12.5px] font-bold text-[#DA2128] transition-all duration-300 hover:bg-[#DA2128] hover:text-white sm:self-center">
                        Read more
                        <FaArrowRight className="h-3 w-3" />
                      </LinkWrapper>
                    </motion.article>
                  );
                })}
              </AnimatePresence>
            ) : (
              <div className="flex flex-col items-center py-16 text-center">
                <div className="grid h-16 w-16 place-items-center rounded-3xl bg-[#FEF7F4]">
                  <FaSearch className="h-7 w-7 text-[#DA2128]" />
                </div>
                <h4 className="mt-5 text-lg font-bold text-stone-900" style={SERIF}>
                  {pressSource.length === 0 ? 'Nothing published yet' : 'No results found'}
                </h4>
                <p className="mt-2 max-w-sm text-[13px] text-stone-500">
                  {pressSource.length === 0
                    ? "This council hasn't shared a press release or coverage item yet."
                    : 'Nothing matches your search. Try a different keyword or clear it to see everything.'}
                </p>
                {pressKeyword && (
                  <button
                    type="button"
                    onClick={() => setPressKeyword('')}
                    className="mt-5 rounded-full bg-gradient-to-r from-[#DA2128] to-[#DB6400] px-6 py-2.5 text-[12.5px] font-bold text-white shadow-lg shadow-[#DA2128]/25 transition-all hover:shadow-[#DA2128]/40"
                  >
                    Clear search
                  </button>
                )}
              </div>
            )}

            {pressTotalPages > 1 && (
              <div className="mt-10 flex items-center justify-center gap-2">
                <button
                  disabled={pressClampedPage === 1}
                  onClick={() => changePressPage(pressClampedPage - 1)}
                  className="grid h-10 w-10 place-items-center rounded-xl text-stone-500 ring-1 ring-stone-200 transition-all hover:text-[#DA2128] hover:ring-[#DA2128] disabled:pointer-events-none disabled:opacity-35"
                  aria-label="Previous page"
                >
                  <FaChevronLeft className="h-4 w-4" />
                </button>
                {Array.from({ length: pressTotalPages }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    onClick={() => changePressPage(n)}
                    className={`relative h-10 w-10 rounded-xl text-[13px] font-extrabold transition-all ${
                      n === pressClampedPage
                        ? 'text-white'
                        : 'text-stone-500 ring-1 ring-stone-200 hover:text-[#DA2128] hover:ring-[#DA2128]'
                    }`}
                  >
                    {n === pressClampedPage && (
                      <motion.span
                        layoutId="press-page-pill"
                        className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#DA2128] to-[#DB6400] shadow-lg shadow-[#DA2128]/30"
                        transition={{ type: 'spring', stiffness: 420, damping: 32 }}
                      />
                    )}
                    <span className="relative">{n}</span>
                  </button>
                ))}
                <button
                  disabled={pressClampedPage === pressTotalPages}
                  onClick={() => changePressPage(pressClampedPage + 1)}
                  className="grid h-10 w-10 place-items-center rounded-xl text-stone-500 ring-1 ring-stone-200 transition-all hover:text-[#DA2128] hover:ring-[#DA2128] disabled:pointer-events-none disabled:opacity-35"
                  aria-label="Next page"
                >
                  <FaChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ============ PUBLICATIONS & REPORTS (same as Sector page + detail page) ============ */}
      <section
        id="publications"
        className="sector-section relative scroll-mt-6 overflow-hidden bg-gradient-to-b from-[#FEF7F4] via-[#FAF7F2] to-[#FEF7F4] py-16 lg:py-20"
      >
        <div className="pointer-events-none absolute -right-24 top-10 h-72 w-72 rounded-full bg-[#F59C00]/10 blur-3xl" />
        <div className="anim-st-blob anim-st-float pointer-events-none absolute -left-24 bottom-10 h-64 w-64 bg-[#DA2128]/10 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
          <div className="mb-12 max-w-2xl">
            {sectionEyebrow('Knowledge Hub', '#DB6400')}
            <h2 className="mt-3 text-2xl font-medium leading-[1.25] tracking-tight text-[#241A16] md:text-4xl" style={SERIF}>
              Publications &amp; Reports
            </h2>
            <p className="mt-4 text-base leading-relaxed text-[#241A16]/75 font-normal">
              Research, policy notes and sector reports prepared by the council to inform decisions and shape the
              state&apos;s growth agenda.
            </p>
          </div>

          {PUBLICATIONS.length > 0 ? (
            <>
              <div className="mt-6 grid gap-7 lg:grid-cols-3">
                {PUBLICATIONS.slice(0, 4).map((p, i) => (
                  <StatePublicationCard key={p.slug || p.title || i} publication={p} index={i} />
                ))}
              </div>

              {PUBLICATIONS.length > 4 && (
                <div className="mt-10 text-center">
                  <Link
                    to="/knowledge/reports"
                    className="st-shine-parent inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#DA2128] to-[#DB6400] px-8 py-3 text-[13px] font-bold text-white shadow-lg shadow-[#DA2128]/30 transition-all hover:-translate-y-0.5 hover:shadow-[#DA2128]/45"
                  >
                    View More Publications
                    <FaArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              )}
            </>
          ) : (
            <div className="mt-6 flex flex-col items-center rounded-[24px] border border-dashed border-[#DA2128]/25 bg-white/60 py-16 text-center">
              <div className="grid h-16 w-16 place-items-center rounded-3xl bg-[#FEF7F4]">
                <FaFileAlt className="h-7 w-7 text-[#DA2128]" />
              </div>
              <h4 className="mt-5 text-lg font-bold text-stone-900" style={SERIF}>
                Publications coming soon
              </h4>
              <p className="mt-2 max-w-sm text-[13px] text-stone-500">
                This council hasn&apos;t published a report yet. Check back soon, or explore other sections above.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ==================== CONTACT US ==================== */}
      <section id="contact" className="relative scroll-mt-20 overflow-hidden bg-gradient-to-b from-[#FAF7F2] to-[#FFFBF7] py-16 lg:py-20">
        <div className="pointer-events-none absolute -right-24 top-10 h-72 w-72 rounded-full bg-[#F59C00]/10 blur-3xl" />
        <div className="anim-st-blob anim-st-float-slow pointer-events-none absolute -left-24 bottom-8 h-64 w-64 bg-[#DA2128]/10 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mb-3 inline-flex items-center gap-2"
              >
                <div className="h-[2px] w-6 rounded-full bg-[#DA2128]" />
                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#DA2128]">Contact</span>
              </motion.div>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="mb-3 text-3xl font-bold leading-[1.1] tracking-tight sm:text-4xl text-stone-900"
                style={SERIF}
              >
                Contact <span className="text-[#DA2128]">Us</span>
              </motion.h2>
              <p className="mb-6 text-base leading-relaxed text-[#241A16]/75 font-normal">
                For queries related to the {name}, reach out to the secretariat. Our team will respond within 48 hours.
              </p>

              <motion.div
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.15 }}
                className="w-full max-w-md rounded-2xl border border-stone-100 bg-white p-5 shadow-sm font-sans"
              >
                <div className="space-y-5">
                  {/* HoD — role label data-driven: ASSOCHAM Regional Head /
                      ASSOCHAM State Head / Council Secretariat */}
                  <div className="flex items-start gap-3.5">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FDF2F2] text-sm font-bold text-[#C0262D]">
                      {(hod?.name || defaultHod.name || 'H').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-[14px] font-extrabold text-stone-900 leading-snug">
                        {hodRoleLabel}
                      </h4>
                      <p className="text-[14px] font-bold text-[#C0262D]">{hod?.name || defaultHod.name}</p>
                      <p className="text-[12px] text-stone-500">{hodSubtitle}</p>
                    </div>
                  </div>
                  {/* Address */}
                  <div className="flex items-start gap-3.5">
                    <a
                      href={mapsUrl(office)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FDF2F2] text-[#C0262D] transition-colors hover:bg-[#C0262D] hover:text-white"
                    >
                      <FaRegBuilding className="h-4 w-4" />
                    </a>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-[14px] font-extrabold text-stone-900 leading-snug">
                        {office.city ? `Address (${office.city})` : 'Address'}
                      </h4>
                      <div className="text-[12.5px] leading-relaxed text-stone-600">
                        {office.lines.map((line, i) => (
                          <span key={i} className="block">{line}</span>
                        ))}
                      </div>
                      <a
                        href={mapsUrl(office)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-[11.5px] font-bold text-[#C0262D] hover:underline"
                      >
                        <FaMapMarkerAlt className="h-3 w-3" />
                        Get directions
                      </a>
                    </div>
                  </div>
                  {/* Email */}
                  <div className="flex items-start gap-3.5">
                    <a
                      href={`mailto:${hodEmail}?subject=${encodeURIComponent(`Query — ${name}`)}`}
                      className="group flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FDF2F2] text-[#C0262D] transition-colors hover:bg-[#C0262D] hover:text-white"
                    >
                      <FaEnvelope className="h-4 w-4" />
                    </a>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-[14px] font-extrabold text-stone-900 leading-snug">Email</h4>
                      <a
                        href={`mailto:${hodEmail}?subject=${encodeURIComponent(`Query — ${name}`)}`}
                        className="block truncate text-[13px] font-bold text-[#C0262D] hover:underline"
                      >
                        {hodEmail}
                      </a>
                      <p className="text-[11.5px] text-stone-500">For queries related to {stateLabel || 'Council'}.</p>
                    </div>
                  </div>
                  {/* Phone */}
                  <div className="flex items-start gap-3.5">
                    <a
                      href={`tel:${hodPhoneHref}`}
                      className="group flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FDF2F2] text-[#C0262D] transition-colors hover:bg-[#C0262D] hover:text-white"
                    >
                      <FaPhone className="h-4 w-4" />
                    </a>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-[14px] font-extrabold text-stone-900 leading-snug">Phone Number</h4>
                      <a href={`tel:${hodPhoneHref}`} className="block text-[13px] font-bold text-[#C0262D] hover:underline">
                        {hodPhone}
                      </a>
                      <p className="text-[11.5px] text-stone-500">Mon–Fri, 9:30 AM – 6:00 PM IST</p>
                    </div>
                  </div>
                  {/* WhatsApp — sirf jab koi named HoD mapped ho */}
                  {hodWaNumber && hasNamedHod && (
                    <div className="pt-1 pl-[50px]">
                      <a
                        href={`https://wa.me/${hodWaNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-[12px] font-bold text-emerald-600 hover:underline"
                      >
                        <FaWhatsapp className="h-3.5 w-3.5" /> WhatsApp
                      </a>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
            <QueryForm />
          </div>
        </div>
      </section>
    </main>
  );
}

/* ==========================================================================
   PUBLICATION CARD — identical to the Sector page card, links to
   /knowledge/reports/{slug} (falls back to the PDF when there is no slug).
   ========================================================================== */
function StatePublicationCard({ publication, index = 0 }) {
  const p = publication || {};
  const path = publicationPath(p);
  const img = resolvePublicationImage(p.img || p.image || p.thumbnail);
  const pdfUrl = p.pdf || p.file || p.pdf_url || null;

  const Wrap = ({ className, children }) => {
    if (path) {
      return (
        <Link to={path} state={{ publication: p }} className={className}>
          {children}
        </Link>
      );
    }
    if (pdfUrl) {
      return (
        <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className={className}>
          {children}
        </a>
      );
    }
    return <span className={className}>{children}</span>;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.11 }}
      className="group relative flex h-full flex-col overflow-hidden rounded-[24px] border border-[#ececec] bg-white shadow-[0_10px_25px_rgba(0,0,0,0.06)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_18px_40px_rgba(0,0,0,0.12)]"
    >
      {/* Gradient header with clip-path */}
      <div
        className="absolute left-0 top-0 z-0 h-[90px] w-full"
        style={{
          background: 'linear-gradient(135deg, #625e5e 0%, #606060 50%, #989292 100%)',
          clipPath: 'polygon(0 0, 100% 0, 100% 72%, 85% 100%, 60% 80%, 35% 100%, 0 72%)',
        }}
      />
      {/* Circular blur element */}
      <div className="absolute right-[18px] top-[18px] z-[1] h-12 w-12 rounded-full bg-white/15 backdrop-blur-sm" />

      {/* Image */}
      <div className="relative z-[2] px-5 pt-[26px]">
        <div className="overflow-hidden rounded-[18px] shadow-[0_8px_20px_rgba(0,0,0,0.12)]">
          <Wrap className="block">
            <img
              src={img}
              alt={p.title || 'Publication'}
              className="w-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          </Wrap>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-[2] flex flex-1 flex-col px-5 pb-[22px] pt-[18px]">
        <Wrap className="block">
          <h3 className="st-clamp-2 mb-1 text-[15px] font-bold leading-snug text-[#222] transition-colors group-hover:text-[#DA2128]" style={SERIF}>
            {p.title}
          </h3>
        </Wrap>
        <div className="mb-[18px] text-xs text-[#888]">{p.date}</div>

        <Wrap className="mt-auto flex w-fit items-center gap-2 rounded-full border-2 border-[#f7941d] bg-white px-[18px] py-[11px] text-xs font-bold text-[#8b0f1a] shadow-[0_6px_14px_rgba(247,148,29,0.25)] transition-all duration-300 hover:bg-[#f7941d] hover:text-white">
          <FaDownload className="h-3 w-3" />
          Download PDF
        </Wrap>
      </div>
    </motion.div>
  );
}

/* ==========================================================================
   EVENT CARD  — sector-style (image based) that links to the event detail page.
   ========================================================================== */
function StateEventCard({ event, index }) {
  const active = isEventUpcoming(event.date);
  const delay = `${Math.min(index * 70, 600)}ms`;
  const { dayPart, monthPart, year } = getDateParts(event.date);
  const imageUrl = resolveEventImage(event.image);
  const detailPath = event.slug ? `/event-details/${event.slug}` : null;

  const content = (
    <article
      style={{ animationDelay: delay }}
      className="event-fade-up group flex flex-col overflow-hidden rounded-[24px] border border-stone-200 bg-white shadow-[0_8px_28px_rgba(28,25,23,0.06)] transition-all duration-500 hover:-translate-y-1 hover:border-[#DA2128]/25 hover:shadow-[0_18px_44px_rgba(218,33,40,0.14)] sm:flex-row"
    >
      {/* Image */}
      <div className="relative h-48 w-full shrink-0 overflow-hidden sm:h-auto sm:w-[280px] lg:w-[300px]">
        <img
          src={imageUrl}
          alt={event.title}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/5 to-black/10" />

        <span className="absolute right-3 top-3 rounded-full bg-stone-900/70 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.14em] text-white backdrop-blur-sm">
          {event.tag || 'Event'}
        </span>

        <div className="absolute bottom-3 left-3.5 leading-none text-white drop-shadow">
          <div className="font-heading text-[2rem] font-black">{dayPart}</div>
          <div className="mt-0.5 text-[11px] font-extrabold uppercase tracking-[0.22em] text-white/90">{monthPart}</div>
        </div>

        <span className="absolute bottom-3 right-3 rounded-lg bg-[#DA2128] px-2.5 py-1 text-[11px] font-extrabold text-white shadow-md">
          {year}
        </span>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col justify-center gap-3.5 p-5 sm:p-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex rounded-full bg-[#FEF2F2] px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#DA2128]">
            {event.tag || 'Event'}
          </span>
          {event.place && (
            <span className="inline-flex rounded-full border border-[#DB6400]/25 bg-[#FFF8EC] px-3 py-1 text-[11px] font-bold text-[#B45309]">
              {event.place}
            </span>
          )}
          {active ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#ECFDF5] px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.08em] text-emerald-600">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
              </span>
              Upcoming
            </span>
          ) : (
            <span className="inline-flex rounded-full border border-stone-300 bg-stone-50 px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.08em] text-stone-500">
              Archived
            </span>
          )}
        </div>

        <h3 className="font-heading text-[1.05rem] font-extrabold leading-snug text-stone-900 transition-colors duration-300 group-hover:text-[#DA2128] sm:text-[1.15rem]">
          {event.title}
        </h3>

        {event.highlight && <p className="text-[13px] leading-relaxed text-stone-500">{event.highlight}</p>}

        <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-[13.5px] font-semibold text-stone-500">
            <FaCalendarAlt className="h-4 w-4 text-stone-400" />
            {event.date}
          </div>

          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-gradient-to-r from-[#DA2128] to-[#C81E24] px-5 py-2.5 text-[13px] font-bold text-white shadow-[0_10px_22px_rgba(218,33,40,0.22)] transition-all duration-300 group-hover:scale-[1.03] group-hover:shadow-[0_14px_28px_rgba(218,33,40,0.3)]">
            <span>{active ? 'Register Interest' : 'View Highlights'}</span>
            <FaArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </div>
      </div>
    </article>
  );

  if (detailPath) {
    return (
      <Link to={detailPath} state={{ event }} className="block no-underline">
        {content}
      </Link>
    );
  }
  return content;
}

/* ========================== ANIMATED COUNTER ========================== */
function CountUp({ value, duration = 1500 }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let started = false;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || started) return;
          started = true;
          const start = performance.now();
          const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setDisplay(Math.round(eased * value));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        });
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [value, duration]);
  return <span ref={ref}>{display}</span>;
}

/* ========================== EVENT CALENDAR BADGE ========================== */
function StateCalendarBadge({ date, active }) {
  const { dayPart, monthPart, year } = getDateParts(date);
  return (
    <div className="relative mx-auto flex w-[88px] shrink-0 flex-col overflow-hidden rounded-[18px] border border-white/80 bg-white shadow-[0_10px_28px_rgba(219,38,40,0.12)] transition-all duration-500 group-hover:-translate-y-1.5 group-hover:rotate-[-1deg] group-hover:shadow-[0_18px_40px_rgba(219,38,40,0.18)] sm:mx-0">
      <div className="absolute left-3 top-0 h-2.5 w-2.5 -translate-y-1/2 rounded-full border border-[#DA2128]/30 bg-[#FEF7F4] transition duration-500 group-hover:bg-[#F59C00]" />
      <div className="absolute right-3 top-0 h-2.5 w-2.5 -translate-y-1/2 rounded-full border border-[#DA2128]/30 bg-[#FEF7F4] transition duration-500 group-hover:bg-[#F59C00]" />
      <div className="relative bg-gradient-to-br from-white to-[#FAF7F2] px-2.5 py-3 text-center">
        <div className="font-heading text-[1.55rem] font-black leading-none text-stone-900" style={SERIF}>{dayPart}</div>
        <div className="mt-1.5 text-[0.65rem] font-extrabold uppercase tracking-[0.22em] text-[#DB6400]">{monthPart}</div>
      </div>
      <div
        className={`relative px-2.5 py-2 text-center text-[0.7rem] font-extrabold tracking-[0.18em] text-white ${
          active ? 'bg-gradient-to-r from-[#DA2128] to-[#F59C00]' : 'bg-gradient-to-r from-stone-500 to-stone-600'
        }`}
      >
        <span className="relative z-10">{year}</span>
      </div>
    </div>
  );
}
